import { useState, useCallback } from 'react';
import { useSnackbar } from "notistack";
import { IApiResponse } from 'api/models/IApiResponse';
import { useTranslation } from 'react-i18next';
import { RoutePaths } from 'constants/RoutePaths';
import { ApiCallFunction } from 'api/models/ApiCallFunction';
import useRefreshToken from 'hooks/useRefreshToken';
import { handleApiError } from 'helpers/handleApiError';

const useApiCall = <RequestType, ResponseType>(
  apiCallFunction: ApiCallFunction<RequestType, IApiResponse<ResponseType>>,
  successMessageKey?: string,
  errorMessageKey?: string,
  useRefreshResponse?: boolean
) => {
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { refreshToken, loading: refreshingToken } = useRefreshToken();

  const handleSuccessMessage = useCallback(() => {
    if (successMessageKey) {
      enqueueSnackbar(t(successMessageKey), { variant: 'success' });
    }
  }, [enqueueSnackbar, successMessageKey, t]);

  const handle401Error = useCallback(
    async (params: RequestType): Promise<IApiResponse<ResponseType>> => {
      const refreshResponse = await refreshToken();

      if (refreshResponse.success) {
        if (useRefreshResponse) {
          return {
            success: true,
            data: refreshResponse.profile as ResponseType,
            status: 200,
            message: 'Profile fetched from refresh token response',
            errors: undefined,
          };
        }
        return await apiCallFunction(params);
      } else {
        window.location.href = RoutePaths.LOGIN;
        return {
          success: false,
          data: undefined,
          status: 401,
          message: 'Unauthorized',
          errors: undefined,
        };
      }
    },
    [refreshToken, apiCallFunction, useRefreshResponse]
  );

  const callAPI = useCallback(
    async (params: RequestType): Promise<IApiResponse<ResponseType>> => {
      try {
        setLoading(true);
        const response = await apiCallFunction(params);
        handleSuccessMessage();
        return response;
      } catch (e: any) {
        if (!refreshingToken && e?.response?.status === 401) {
          return await handle401Error(params);
        }

        if (errorMessageKey) {
          enqueueSnackbar(t(errorMessageKey), { variant: 'error' });
        }

        return handleApiError(e, { t, enqueueSnackbar });
      } finally {
        setLoading(false);
      }
    },
    [
      apiCallFunction,
      handleSuccessMessage,
      handle401Error,
      enqueueSnackbar,
      t,
      refreshingToken,
      errorMessageKey,
    ]
  );

  return { callAPI, loading };
};

export default useApiCall;